import { cn } from '@/lib/utils';
import type { DayMicroBar as DayMicroBarGeometry } from '@/pages/settling-in/buildDayMicroBar';

interface DayMicroBarProps {
  bar: DayMicroBarGeometry;
  className?: string;
}

/**
 * Decorative two-shade micro-bar for a Days row (ST-6/S20).
 *
 * Light segment = the planned window, dark segment = actual attendance, both on
 * a shared day track — so "arrived late / left early" is visible without reading
 * digits.
 *
 * DECORATIVE BY CONSTRUCTION: the numeric times and the Variance column remain
 * the source of truth. The bar adds no information the row does not already
 * state numerically and carries NO colour-only meaning, so it is `aria-hidden`
 * and contributes no accessible name.
 *
 * Both shades are the same hue from the existing gated `--capacity-*` family —
 * no `settling-*` token family is introduced.
 */
export function DayMicroBar({ bar, className }: DayMicroBarProps) {
  // Neither side has a window → render nothing, rather than a zero-width artifact.
  if (!bar.planned && !bar.actual) return null;

  // No min-width: S22 requires wrap-tolerant sizing so the German catalog cannot
  // force horizontal overflow. The bar is decorative and takes whatever width the
  // column gives it.
  //
  // The runtime percentages (computed from the day timeline) are carried by SVG
  // `x`/`width` GEOMETRY ATTRIBUTES, not by an inline `style` — geometry
  // attributes are not CSS, so the no-inline-style rule is satisfied without
  // introducing a per-row generated class. The <svg> carries no viewBox, so one
  // user unit is one CSS pixel and `rx="2"` is exactly Tailwind's `rounded-sm`;
  // colour and box sizing stay on Tailwind + cn().
  return (
    <div
      className={cn('relative h-1.5 w-full rounded-sm bg-muted', className)}
      aria-hidden="true"
      data-testid="day-micro-bar"
    >
      <svg className="absolute inset-0 h-full w-full" focusable="false">
        {bar.planned && (
          <rect
            x={`${bar.planned.offsetPct}%`}
            y="0"
            width={`${bar.planned.widthPct}%`}
            height="100%"
            rx="2"
            className="fill-capacity-normal/30"
            data-testid="day-micro-bar-planned"
          />
        )}
        {bar.actual && (
          <rect
            x={`${bar.actual.offsetPct}%`}
            y="0"
            width={`${bar.actual.widthPct}%`}
            height="100%"
            rx="2"
            className="fill-capacity-normal"
            data-testid="day-micro-bar-actual"
          />
        )}
      </svg>
    </div>
  );
}
